import sum from 'lodash/sum';
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  isLoading: false,
  error: null,
  products: [],
  product: null,
  checkout: {
    activeStep: 0,
    cart: [],
    subtotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
    totalItems: 0,
    // Ajout de la gestion des codes promo
    appliedPromoCode: null,
    promoCodeDiscount: 0,
  },
};

const slice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET PRODUCTS
    getProductsSuccess(state, action) {
      state.isLoading = false;
      state.products = action.payload;
    },

    // GET PRODUCT
    getProductSuccess(state, action) {
      state.isLoading = false;
      state.product = action.payload;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart = action.payload;

      const totalItems = sum(
        cart.map((product) => sum(product.orderItems.map((i) => i.quantity)))
      );
      const subtotal = sum(
        cart.map(
          (product) =>
            product.price.replace(',', '') *
            sum(product.orderItems.map((i) => i.quantity))
        )
      );
      state.checkout.cart = cart;
      state.checkout.subtotal = subtotal;
      state.checkout.total =
        subtotal -
        state.checkout.discount -
        state.checkout.promoCodeDiscount +
        state.checkout.shipping;
      state.checkout.totalItems = totalItems;
    },

    addToCart(state, action) {
      const newProduct = action.payload;
      const isEmptyCart = !state.checkout.cart.length;

      if (isEmptyCart) {
        state.checkout.cart = [newProduct];
      } else {
        const existingProductIndex = state.checkout.cart.findIndex(
          (product) => product.id === newProduct.id
        );

        if (existingProductIndex !== -1) {
          // Product exists, update its orderItems
          const existingProduct = state.checkout.cart[existingProductIndex];

          // Create a deep copy of the existing product
          const updatedProduct = {
            ...existingProduct,
            orderItems: [...existingProduct.orderItems],
          };

          // Merge the orderItems from both products or update quantities
          newProduct.orderItems.forEach((newItem) => {
            const existingItemIndex = updatedProduct.orderItems.findIndex(
              (item) =>
                item.size === newItem.size && item.color === newItem.color
            );

            if (existingItemIndex !== -1) {
              // Item with same size and color exists, update quantity
              updatedProduct.orderItems[existingItemIndex].quantity +=
                newItem.quantity;
            } else {
              // Item with this size/color doesn't exist, add it
              updatedProduct.orderItems.push(newItem);
            }
          });

          // Replace the product in the cart
          const updatedCart = [...state.checkout.cart];
          updatedCart[existingProductIndex] = updatedProduct;
          state.checkout.cart = updatedCart;
        } else {
          // Product doesn't exist in cart, add it
          state.checkout.cart = [...state.checkout.cart, newProduct];
        }
      }

      // Calculate totalItems correctly
      state.checkout.totalItems = sum(
        state.checkout.cart.map((product) =>
          sum(product.orderItems.map((item) => item.quantity || 0))
        )
      );

      // Calculate subtotal and total
      const subtotal = sum(
        state.checkout.cart.map(
          (product) =>
            parseFloat(product.price.replace(',', '')) *
            sum(product.orderItems.map((i) => i.quantity || 0))
        )
      );

      state.checkout.subtotal = subtotal;
      state.checkout.total =
        subtotal -
        state.checkout.discount -
        state.checkout.promoCodeDiscount +
        state.checkout.shipping;
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter(
        (product) => product.id !== action.payload
      );

      state.checkout.cart = updateCart;
    },

    resetCart(state) {
      state.checkout.cart = [];
      state.checkout.billing = null;
      state.checkout.activeStep = 0;
      state.checkout.total = 0;
      state.checkout.subtotal = 0;
      state.checkout.totalItems = 0;
      state.checkout.appliedPromoCode = null;
      state.checkout.promoCodeDiscount = 0;
    },

    backStep(state) {
      state.checkout.activeStep -= 1;
    },

    nextStep(state) {
      state.checkout.activeStep += 1;
    },

    gotoStep(state, action) {
      const step = action.payload;
      state.checkout.activeStep = step;
    },

    increaseQuantity(state, action) {
      const productId = action.payload;

      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity + 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    decreaseQuantity(state, action) {
      const productId = action.payload;
      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          return {
            ...product,
            quantity: product.quantity - 1,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;
      state.checkout.discount = discount;
      state.checkout.total =
        state.checkout.subtotal -
        discount -
        state.checkout.promoCodeDiscount +
        state.checkout.shipping;
    },

    applyShipping(state, action) {
      const shipping = action.payload;
      state.checkout.shipping = shipping;
      state.checkout.total =
        state.checkout.subtotal -
        state.checkout.discount -
        state.checkout.promoCodeDiscount +
        shipping;
    },

    // Actions pour les codes promo
    applyPromoCode(state, action) {
      const { promoCode, discountAmount } = action.payload;
      state.checkout.appliedPromoCode = promoCode;
      state.checkout.promoCodeDiscount = discountAmount;
      // Recalculer le total
      state.checkout.total =
        state.checkout.subtotal -
        state.checkout.discount -
        discountAmount +
        state.checkout.shipping;
    },

    removePromoCode(state) {
      state.checkout.appliedPromoCode = null;
      state.checkout.promoCodeDiscount = 0;
      // Recalculer le total
      state.checkout.total =
        state.checkout.subtotal -
        state.checkout.discount +
        state.checkout.shipping;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  addToCart,
  resetCart,
  gotoStep,
  backStep,
  nextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  increaseQuantity,
  decreaseQuantity,
  applyPromoCode,
  removePromoCode,
} = slice.actions;
